// src/VacMapWidget.tsx
// Виджет 3 дашборда «Вакцинация» — карта Карасайского района с зонами амбулаторий.
//
// Это перенос утверждённого эталона vac_map.html (CARTA_SPEC.md, утв. 25.07.2026).
// Поведение сохранено 1:1: мерцание зон своим цветом вразнобой, задержка 1,5 с перед
// раскрытием, плавный рост к центру, непрозрачность и вывод поверх, удержание 3 с,
// эстафета (новая зона ждёт сворачивания предыдущей), точка и подпись центра амбулатории.
// Геометрия (Вороной по 23 амбулаториям) и границы района — из /vac_map_geo.json.
//
// Отличие от эталона одно: цвет зоны берётся из реальных данных (w3_map.facilities),
// а не из демонстрационных. МИС отдаёт охват по УЧАСТКАМ, а не по амбулаториям,
// поэтому участки сворачиваются в амбулаторию по справочнику area_facility из того же
// файла. Для зоны без данных — серый цвет.
import { useEffect, useMemo, useRef, useState } from "react";

const NS = "http://www.w3.org/2000/svg";
const VW = 1000, VH = 760;

export type MapFacilityRow = {
  name: string;
  people?: number | null;
  population?: number | null;
  coverage?: number | null;
};

type GeoFacility = { name: string; lat: number; lng: number; uch: number };
type GeoJsonPoly = { type: string; coordinates: any };
type Geo = {
  district: string;
  boundary: GeoJsonPoly;
  neighbors: { name?: string; geojson: GeoJsonPoly }[];
  facilities: GeoFacility[];
  area_facility: Record<string, string>;
};

// один загруженный файл на всю страницу (виджет монтируется дважды: компактный и развёрнутый)
let geoPromise: Promise<Geo> | null = null;
function loadGeo(): Promise<Geo> {
  if (!geoPromise) {
    geoPromise = fetch("/vac_map_geo.json", { credentials: "omit" }).then((r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json() as Promise<Geo>;
    });
  }
  return geoPromise;
}

// «- 149 общей практики» и «149  общей практики» — один и тот же участок
function normArea(s: string): string {
  return String(s || "").replace(/^[\s\-–—]+/, "").replace(/\s+/g, " ").trim().toLowerCase();
}

/** Охват по амбулаториям из строк w3_map.facilities (они приходят по участкам). */
export function coverageByFacility(
  rows: MapFacilityRow[],
  areaFacility: Record<string, string>,
  facilityNames: string[],
): Record<string, number | null> {
  const byNormArea: Record<string, string> = {};
  Object.entries(areaFacility || {}).forEach(([area, fac]) => { byNormArea[normArea(area)] = fac; });
  const byNormFac: Record<string, string> = {};
  facilityNames.forEach((f) => { byNormFac[normArea(f)] = f; });

  const acc: Record<string, { people: number; pop: number; direct: number | null }> = {};
  const bump = (fac: string) => (acc[fac] = acc[fac] || { people: 0, pop: 0, direct: null });

  (rows || []).forEach((r) => {
    const key = normArea(r.name);
    // строка уже по амбулатории (если бэкенд начнёт агрегировать сам) — берём её охват
    const direct = byNormFac[key];
    if (direct) {
      const a = bump(direct);
      if (r.coverage !== null && r.coverage !== undefined) a.direct = r.coverage;
      a.people += Number(r.people || 0);
      a.pop += Number(r.population || 0);
      return;
    }
    const fac = byNormArea[key];
    if (!fac) return;
    const a = bump(fac);
    a.people += Number(r.people || 0);
    a.pop += Number(r.population || 0);
  });

  const out: Record<string, number | null> = {};
  facilityNames.forEach((f) => {
    const a = acc[f];
    if (!a) { out[f] = null; return; }
    if (a.direct !== null) { out[f] = a.direct; return; }
    out[f] = a.pop > 0 ? Math.round((a.people * 1000) / a.pop) / 10 : null;
  });
  return out;
}

const CSS = `
.vacmap { position:relative; display:flex; gap:10px; width:100%; min-height:0; }
.vacmap .mapbox { position:relative; flex:1 1 auto; min-width:0; background:#0a1929; border-radius:8px; overflow:hidden; }
.vacmap svg { width:100%; height:100%; display:block; }
.vacmap .cell { cursor:pointer; transition:fill-opacity .12s, transform .42s cubic-bezier(.22,.61,.36,1);
  transform-box:fill-box; transform-origin:center; animation:vacflare var(--dur,2.4s) ease-in-out infinite;
  animation-delay:var(--dly,0s); }
.vacmap .cell.zoom { stroke-width:2.2; }
.vacmap .focusmk { opacity:0; transition:opacity .35s; pointer-events:none; }
.vacmap .flbl { font-size:18px; fill:#fff; paint-order:stroke; stroke:rgba(8,17,29,.7); stroke-width:4px;
  stroke-linejoin:round; font-weight:700; }
@keyframes vacflare { 0%,100% { filter:brightness(.78) saturate(1.15); } 50% { filter:brightness(1.85) saturate(1.8); } }
.vacmap .clbl { font-size:10.5px; fill:#dce6f2; paint-order:stroke; stroke:#0a1929; stroke-width:2.4px;
  stroke-linejoin:round; pointer-events:none; font-weight:600; opacity:.92; }
.vacmap .tip { position:absolute; pointer-events:none; background:#16304d; border:1px solid #233f5d;
  border-radius:9px; padding:8px 11px; font-size:13px; color:#e6eef7; box-shadow:0 10px 34px rgba(0,0,0,.42);
  z-index:5; max-width:240px; }
.vacmap .tip b { display:block; margin-bottom:3px; }
.vacmap .tip .cvv { font-variant-numeric:tabular-nums; font-weight:700; }
.vacmap .listbox { flex:0 0 240px; overflow-y:auto; max-height:100%; padding:2px 4px; }
.vacmap .row { display:grid; grid-template-columns:12px 1fr auto auto; gap:9px; align-items:center;
  padding:6px 7px; border-radius:8px; cursor:pointer; }
.vacmap .row:hover, .vacmap .row.active { background:#16304d; }
.vacmap .row .rdot { width:11px; height:11px; border-radius:3px; }
.vacmap .row .nm { font-size:12.5px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; color:#c3d2e4; }
.vacmap .row .cv { font-variant-numeric:tabular-nums; font-weight:600; text-align:right; min-width:48px; font-size:12.5px; }
.vacmap .row .uc { font-variant-numeric:tabular-nums; color:#7d93ad; text-align:right; min-width:22px; font-size:12px; }
@media (prefers-reduced-motion:reduce) {
  .vacmap * { transition:none !important; }
  .vacmap .cell { animation:none; opacity:1; filter:none; }
}
`;

const C_GREEN = "#25c274", C_YELLOW = "#f5b423", C_RED = "#f0554f", C_GREY = "#5a6b80";

// ── ЦВЕТ ЗОНЫ ────────────────────────────────────────────────────────────────
// Было из спеки: >95% зелёный, 80–95% жёлтый, <80% красный. Эти пороги придуманы
// до того, как охват начали считать по календарю. На реальных цифрах (замер
// 12.08.2026: район 33,6%, амбулатории 24–41%) вся карта красная — цвет не
// различает ничего. Утверждённой целевой цифры у заказчика нет, поэтому любой
// абсолютный порог был бы выдуман заново.
//
// Красим ОТНОСИТЕЛЬНО РАЙОНА: зелёный — заметно лучше среднего, красный — заметно
// хуже, жёлтый — как район. Ширина полосы «как район» — COVERAGE_BAND, одно число.
// Без ориентира (район ещё не посчитан) зона серая, а не красная: «нет ориентира»
// и «плохо» — разные вещи, и путать их на карте нельзя.
const COVERAGE_BAND = 0.15;
function colBy(c: number | null | undefined, benchmark: number | null | undefined): string {
  if (c === null || c === undefined || !Number.isFinite(c)) return C_GREY;
  if (benchmark === null || benchmark === undefined || !Number.isFinite(benchmark)
      || benchmark <= 0) return C_GREY;
  if (c >= benchmark * (1 + COVERAGE_BAND)) return C_GREEN;
  if (c >= benchmark * (1 - COVERAGE_BAND)) return C_YELLOW;
  return C_RED;
}
function pctText(c: number | null | undefined): string {
  if (c === null || c === undefined || !Number.isFinite(c)) return "—";
  return c.toFixed(1).replace(".", ",") + "%";
}

export default function VacMapWidget({ rows, expanded, showList, benchmark, onZoneClick }: {
  rows: MapFacilityRow[]; expanded: boolean; showList: boolean;
  /** охват по району — ориентир для цвета зон (см. colBy) */
  benchmark?: number | null;
  /** клик по зоне карты — открыть табличную часть по амбулатории */
  onZoneClick?: (facility: string) => void }): JSX.Element {
  const [geo, setGeo] = useState<Geo | null>(null);
  const [err, setErr] = useState("");
  const boxRef = useRef<HTMLDivElement | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const tipRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const uid = useMemo(() => "vacmap" + Math.random().toString(36).slice(2, 8), []);

  useEffect(() => {
    let alive = true;
    loadGeo().then((g) => { if (alive) setGeo(g); }).catch((e) => alive && setErr(String(e?.message || e)));
    return () => { alive = false; };
  }, []);

  const cov = useMemo(() => {
    if (!geo) return {} as Record<string, number | null>;
    return coverageByFacility(rows || [], geo.area_facility, geo.facilities.map((f) => f.name));
  }, [geo, rows]);

  // Карта строится императивно и один раз, поэтому обработчик клика запомнил бы фильтры
  // на момент построения. Держим ссылку свежей через ref — иначе таблица открывалась бы
  // со старым периодом или участком.
  const zoneClickRef = useRef(onZoneClick);
  zoneClickRef.current = onZoneClick;
  // Ориентир держим в ref по той же причине, что и обработчик клика: карта строится
  // императивно, и замыкание запомнило бы значение на момент построения.
  const benchRef = useRef<number | null | undefined>(benchmark);
  benchRef.current = benchmark;

  // Всё построение — императивное, ровно как в эталоне (см. CARTA_SPEC.md).
  useEffect(() => {
    const svg = svgRef.current, tip = tipRef.current;
    if (!geo || !svg || !tip) return;
    const timers: number[] = [];
    while (svg.firstChild) svg.removeChild(svg.firstChild);
    const list = listRef.current;
    if (list) while (list.firstChild) list.removeChild(list.firstChild);

    const kr: number[][][][] = geo.boundary.type === "Polygon" ? [geo.boundary.coordinates] : geo.boundary.coordinates;
    const kpts: number[][] = [];
    kr.forEach((poly) => poly.forEach((ring) => ring.forEach((p) => kpts.push(p))));
    const la = kpts.map((p) => p[1]), lo = kpts.map((p) => p[0]);
    let minLat = Math.min(...la), maxLat = Math.max(...la), minLng = Math.min(...lo), maxLng = Math.max(...lo);
    // Поле вокруг района: было 5% — район терялся среди пустоты. Оставляем 1%,
    // чтобы полигон занимал почти всю область рисования.
    const MG = 0.01;
    const dLat = (maxLat - minLat) * MG, dLng = (maxLng - minLng) * MG;
    minLat -= dLat; maxLat += dLat; minLng -= dLng; maxLng += dLng;
    const lat0 = (minLat + maxLat) / 2, kxf = Math.cos((lat0 * Math.PI) / 180);
    const spanX = (maxLng - minLng) * kxf, spanY = maxLat - minLat;
    // Высота области рисования подгоняется под ФОРМУ РАЙОНА. Раньше стояло жёсткое
    // 1000×760, район в него не вписывался, и сверху-снизу оставались пустые полосы —
    // на экране это выглядело как «карта занимает половину контейнера».
    const vh = Math.max(200, Math.round((VW * spanY) / spanX));
    svg.setAttribute("viewBox", `0 0 ${VW} ${vh}`);
    const sc = Math.min(VW / spanX, vh / spanY);
    const offX = (VW - spanX * sc) / 2, offY = (vh - spanY * sc) / 2;
    const proj = (lng: number, lat: number): [number, number] =>
      [offX + (lng - minLng) * kxf * sc, offY + (maxLat - lat) * sc];

    const clip = document.createElementNS(NS, "clipPath");
    clip.id = uid + "-vp";
    const cr = document.createElementNS(NS, "rect");
    cr.setAttribute("width", String(VW)); cr.setAttribute("height", String(vh));
    clip.appendChild(cr); svg.appendChild(clip);
    const root = document.createElementNS(NS, "g");
    root.setAttribute("clip-path", `url(#${uid}-vp)`);
    svg.appendChild(root);

    const ringPath = (ring: number[][]) => ring.map((p, i) => {
      const [x, y] = proj(p[0], p[1]);
      return (i ? "L" : "M") + x.toFixed(1) + " " + y.toFixed(1);
    }).join(" ") + "Z";
    const polysOf = (gj: GeoJsonPoly): number[][][][] => (gj.type === "Polygon" ? [gj.coordinates] : gj.coordinates);

    (geo.neighbors || []).forEach((n) => polysOf(n.geojson).forEach((poly: any) => {
      const p = document.createElementNS(NS, "path");
      p.setAttribute("d", poly.map(ringPath).join(" "));
      p.setAttribute("fill", "#22303f"); p.setAttribute("stroke", "#2b3b4d");
      p.setAttribute("stroke-width", "1"); p.setAttribute("fill-rule", "evenodd");
      root.appendChild(p);
    }));

    const kOuter = kr[0][0];
    const base = document.createElementNS(NS, "path");
    base.setAttribute("d", kr.map((poly) => poly.map(ringPath).join(" ")).join(" "));
    base.setAttribute("fill", "#0e2136"); base.setAttribute("fill-rule", "evenodd");
    root.appendChild(base);

    const ringXY = kOuter.map((p) => { const [x, y] = proj(p[0], p[1]); return { x, y }; });
    type Pt = { x: number; y: number };
    const ptSeg = (px: number, py: number, a: Pt, b: Pt) => {
      const dx = b.x - a.x, dy = b.y - a.y; const l = dx * dx + dy * dy || 1;
      let t = ((px - a.x) * dx + (py - a.y) * dy) / l; t = Math.max(0, Math.min(1, t));
      return Math.hypot(px - (a.x + t * dx), py - (a.y + t * dy));
    };
    const distRing = (px: number, py: number) => {
      let m = 1e9;
      for (let i = 0; i < ringXY.length - 1; i++) { const dd = ptSeg(px, py, ringXY[i], ringXY[i + 1]); if (dd < m) m = dd; }
      return m;
    };
    const rnd = (s: number) => { const x = Math.sin(s) * 43758.5453; return x - Math.floor(x); };
    // волнистая граница: фаза от канонического порядка концов ребра — обе смежные зоны
    // рисуют одинаковую кривую, между ними не появляется щели
    const wavyEdge = (A: Pt, B: Pt): Pt[] => {
      let P0 = A, P1 = B, rev = false;
      if (A.x > B.x || (A.x === B.x && A.y > B.y)) { P0 = B; P1 = A; rev = true; }
      const len = Math.hypot(P1.x - P0.x, P1.y - P0.y);
      const mid = distRing((P0.x + P1.x) / 2, (P0.y + P1.y) / 2);
      if (mid < 3.0 || len < 10) return [];   // граница района или короткое ребро — прямая
      const seed = Math.round(P0.x) * 13.1 + Math.round(P0.y) * 7.3 + Math.round(P1.x) * 3.7 + Math.round(P1.y) * 17.1;
      const waves = 2 + Math.floor(rnd(seed) * 3);
      const phase = rnd(seed * 1.7) * Math.PI * 2;
      const amp = Math.min(len * 0.11, 7.5) * (0.6 + rnd(seed * 2.3) * 0.8);
      const ux = (P1.x - P0.x) / len, uy = (P1.y - P0.y) / len, nx = -uy, ny = ux;
      const N = 16, pts: Pt[] = [];
      for (let k = 1; k < N; k++) {
        const t = k / N;
        const off = amp * Math.sin(t * Math.PI) * Math.sin(t * Math.PI * waves + phase);
        pts.push({ x: P0.x + ux * len * t + nx * off, y: P0.y + uy * len * t + ny * off });
      }
      return rev ? pts.reverse() : pts;
    };

    // Вороной
    const sites = geo.facilities.map((f) => { const [x, y] = proj(f.lng, f.lat); return { x, y, f }; });
    const subjPoly = kOuter.map((p) => { const [x, y] = proj(p[0], p[1]); return { x, y }; });
    const clipHalf = (poly: Pt[], si: Pt, sj: Pt): Pt[] => {
      const mx = (si.x + sj.x) / 2, my = (si.y + sj.y) / 2, dx = sj.x - si.x, dy = sj.y - si.y;
      const inside = (p: Pt) => ((p.x - mx) * dx + (p.y - my) * dy) <= 0;
      const out: Pt[] = []; const n = poly.length;
      for (let i = 0; i < n; i++) {
        const A = poly[i], B = poly[(i + 1) % n]; const ia = inside(A), ib = inside(B);
        if (ia) out.push(A);
        if (ia !== ib) {
          const den = ((B.x - A.x) * dx + (B.y - A.y) * dy);
          const t = den ? ((mx - A.x) * dx + (my - A.y) * dy) / den : 0;
          out.push({ x: A.x + t * (B.x - A.x), y: A.y + t * (B.y - A.y) });
        }
      }
      return out;
    };

    const cellEls: Record<number, SVGPathElement> = {};
    const rowEls: Record<number, HTMLDivElement> = {};
    const covOf = (i: number): number | null => {
      const v = cov[sites[i].f.name];
      return v === undefined ? null : v;
    };

    sites.forEach((si, i) => {
      let poly = subjPoly.slice();
      sites.forEach((sj, j) => { if (i !== j && poly.length) poly = clipHalf(poly, si, sj); });
      if (poly.length < 3) return;
      let d = ""; const n = poly.length;
      for (let k = 0; k < n; k++) {
        const A = poly[k], B = poly[(k + 1) % n];
        if (k === 0) d = "M" + A.x.toFixed(1) + " " + A.y.toFixed(1);
        wavyEdge(A, B).forEach((p) => { d += " L" + p.x.toFixed(1) + " " + p.y.toFixed(1); });
        d += " L" + B.x.toFixed(1) + " " + B.y.toFixed(1);
      }
      d += "Z";
      const path = document.createElementNS(NS, "path");
      path.setAttribute("class", "cell"); path.setAttribute("d", d);
      path.setAttribute("fill", colBy(covOf(i), benchRef.current)); path.setAttribute("fill-opacity", ".92");
      path.setAttribute("stroke", "#0a1929"); path.setAttribute("stroke-width", "1.4");
      path.setAttribute("stroke-linejoin", "round");
      root.appendChild(path); cellEls[i] = path;
      path.style.setProperty("--dur", (1.4 + Math.random() * 2.0).toFixed(2) + "s");
      path.style.setProperty("--dly", (-Math.random() * 4).toFixed(2) + "s");
      path.addEventListener("mousemove", (e) => showTip(i, e as MouseEvent));
      path.addEventListener("mouseenter", () => { hl(i, true); zoomEnter(i); });
      path.addEventListener("mouseleave", () => { tip.hidden = true; hl(i, false); zoomLeave(i); });
      path.addEventListener("click", () => {
        const r = rowEls[i];
        if (r) r.scrollIntoView({ block: "nearest" });
        flash(i);
        // Клик по зоне открывает табличную часть по этой амбулатории. Раньше он только
        // подсвечивал строку в списке — drill с карты не работал вовсе.
        const f = geo?.facilities?.[i];
        if (f) zoneClickRef.current?.(f.name);
      });
    });

    const outline = document.createElementNS(NS, "path");
    outline.setAttribute("d", kr.map((poly) => poly.map(ringPath).join(" ")).join(" "));
    outline.setAttribute("fill", "none"); outline.setAttribute("stroke", "#4b9fe8");
    outline.setAttribute("stroke-width", "2.6"); outline.setAttribute("fill-rule", "evenodd");
    root.appendChild(outline);

    sites.forEach((si) => {
      const t = document.createElementNS(NS, "text");
      t.setAttribute("class", "clbl");
      t.setAttribute("x", String(si.x)); t.setAttribute("y", String(si.y - 9));
      t.setAttribute("text-anchor", "middle");
      t.textContent = si.f.name.replace("ПМСП ", "").replace("ВА ", "");
      root.appendChild(t);
      const dot = document.createElementNS(NS, "circle");
      dot.setAttribute("cx", String(si.x)); dot.setAttribute("cy", String(si.y));
      dot.setAttribute("r", "2.6"); dot.setAttribute("fill", "#dce6f2"); dot.setAttribute("opacity", ".75");
      root.appendChild(dot);
    });

    // маркер центра амбулатории на раскрытой зоне
    const fdot = document.createElementNS(NS, "circle");
    fdot.setAttribute("r", "4.6"); fdot.setAttribute("fill", "#fff");
    fdot.setAttribute("stroke", "#08111d"); fdot.setAttribute("stroke-width", "1.6");
    const ftxt = document.createElementNS(NS, "text");
    ftxt.setAttribute("class", "flbl"); ftxt.setAttribute("text-anchor", "middle");
    const fg = document.createElementNS(NS, "g");
    fg.setAttribute("class", "focusmk"); fg.style.display = "none";
    fg.appendChild(fdot); fg.appendChild(ftxt); root.appendChild(fg);

    function showTip(i: number, e: MouseEvent) {
      const f = geo!.facilities[i]; const c = covOf(i);
      tip!.hidden = false;
      tip!.innerHTML = "<b></b>Охват <span class=\"cvv\"></span> · участков <span class=\"uch\"></span>";
      (tip!.querySelector("b") as HTMLElement).textContent = f.name;
      const cvv = tip!.querySelector(".cvv") as HTMLElement;
      cvv.textContent = pctText(c); cvv.style.color = colBy(c, benchRef.current);
      (tip!.querySelector(".uch") as HTMLElement).textContent = String(f.uch);
      const r = svg!.getBoundingClientRect();
      const px = e.clientX - r.left, py = e.clientY - r.top;
      tip!.style.left = Math.min(px + 14, r.width - tip!.offsetWidth - 8) + "px";
      tip!.style.top = Math.max(py - 10, 6) + "px";
    }

    if (list) {
      geo.facilities.forEach((f, i) => {
        const c = cov[f.name] ?? null;
        const row = document.createElement("div");
        row.className = "row";
        const dot = document.createElement("span"); dot.className = "rdot"; dot.style.background = colBy(c, benchRef.current);
        const nm = document.createElement("span"); nm.className = "nm"; nm.textContent = f.name; nm.title = f.name;
        const cv = document.createElement("span"); cv.className = "cv"; cv.style.color = colBy(c, benchRef.current); cv.textContent = pctText(c);
        const uc = document.createElement("span"); uc.className = "uc"; uc.textContent = String(f.uch);
        row.append(dot, nm, cv, uc);
        row.addEventListener("mouseenter", () => hl(i, true));
        row.addEventListener("mouseleave", () => hl(i, false));
        // Клик по строке списка открывает таблицу — как и клик по самой зоне.
        // Раньше строка только подсвечивала зону, и drill из списка не работал.
        row.style.cursor = "pointer";
        row.title = `${f.name} — открыть список прививок`;
        row.addEventListener("click", () => {
          flash(i);
          zoneClickRef.current?.(f.name);
        });
        list.appendChild(row); rowEls[i] = row;
      });
    }

    // ── контроллер раскрытия зон (CARTA_SPEC §«Интерактив зон», пункты 1–8) ──
    const Z_DUR = 420, Z_HOLD = 3000, Z_OPEN = 1500;
    let zTarget = -1, zShown = -1, zHoldUntil = 0, zBusyUntil = 0;
    let zEnterTimer: number | null = null, zLeaveTimer: number | null = null;

    function zExpand(i: number) {
      const p = cellEls[i]; if (!p) return;
      const b = p.getBBox();
      const cx = b.x + b.width / 2, cy = b.y + b.height / 2;
      let k = Math.min((VW * 0.9) / b.width, (vh * 0.9) / b.height);
      k = Math.max(1.7, Math.min(k, 7));
      root.appendChild(p); p.classList.add("zoom"); p.setAttribute("fill-opacity", "1");
      p.getBoundingClientRect();   // форс reflow, иначе зона выскакивает рывком
      requestAnimationFrame(() => {
        if (zTarget !== i) return;
        p.style.transform = `translate(${(VW / 2 - cx).toFixed(1)}px,${(vh / 2 - cy).toFixed(1)}px) scale(${k.toFixed(2)})`;
      });
      zShown = i; zHoldUntil = Date.now() + Z_HOLD; zBusyUntil = Date.now() + Z_DUR;
      const ss = sites[i];
      const mx = k * (ss.x - cx) + VW / 2, my = k * (ss.y - cy) + vh / 2;
      fdot.setAttribute("cx", mx.toFixed(1)); fdot.setAttribute("cy", my.toFixed(1));
      ftxt.setAttribute("x", mx.toFixed(1)); ftxt.setAttribute("y", (my - 11).toFixed(1));
      ftxt.textContent = ss.f.name.replace("ПМСП ", "").replace("ВА ", "");
      fg.style.display = ""; root.appendChild(fg); fg.getBoundingClientRect();
      requestAnimationFrame(() => { if (zShown === i) fg.style.opacity = "1"; });
    }
    function zShrink(i: number) {
      const p = cellEls[i]; if (!p) return;
      p.style.transform = ""; fg.style.opacity = "0";
      zBusyUntil = Date.now() + Z_DUR;
      if (zShown === i) zShown = -1;
      timers.push(window.setTimeout(() => {
        if (zTarget === i || zShown === i) return;
        p.classList.remove("zoom"); p.setAttribute("fill-opacity", ".92"); fg.style.display = "none";
        if (outline && outline.parentNode === root) root.insertBefore(p, outline);
      }, Z_DUR + 40));
    }
    function zoomEnter(i: number) {
      if (zLeaveTimer) { clearTimeout(zLeaveTimer); zLeaveTimer = null; }
      if (zTarget === i) return;
      const prev = zTarget; zTarget = i;
      if (zEnterTimer) { clearTimeout(zEnterTimer); zEnterTimer = null; }
      if (prev !== -1 && zShown === prev) zShrink(prev);
      // эстафета: новая зона ждёт, пока свернётся предыдущая
      const wait = Math.max(Z_OPEN, zBusyUntil - Date.now() + 40);
      const th = window.setTimeout(() => { if (zTarget === i) zExpand(i); }, wait);
      zEnterTimer = th; timers.push(th);
    }
    function zoomLeave(i: number) {
      if (zTarget !== i) return;
      if (zEnterTimer) { clearTimeout(zEnterTimer); zEnterTimer = null; }
      const delay = Math.max(140, zHoldUntil - Date.now());   // удержание 3 с
      if (zLeaveTimer) clearTimeout(zLeaveTimer);
      const th = window.setTimeout(() => {
        if (zTarget === i) { zTarget = -1; if (zShown === i) zShrink(i); }
      }, delay);
      zLeaveTimer = th; timers.push(th);
    }
    function hl(i: number, on: boolean) {
      const c = cellEls[i];
      if (c) c.setAttribute("fill-opacity", on ? "1" : (c.classList.contains("zoom") ? "1" : ".92"));
      const row = rowEls[i];
      if (row) row.classList.toggle("active", on);
    }
    let ft = 0;
    function flash(i: number) {
      hl(i, true); clearTimeout(ft);
      ft = window.setTimeout(() => hl(i, false), 1100);
      timers.push(ft);
    }

    return () => {
      timers.forEach((t) => clearTimeout(t));
      if (zEnterTimer) clearTimeout(zEnterTimer);
      if (zLeaveTimer) clearTimeout(zLeaveTimer);
    };
  // benchmark в зависимостях обязателен: карточки и виджеты грузятся РАЗНЫМИ
  // запросами, и ориентир приходит позже карты. Без него карта осталась бы
  // построенной с benchmark=null, то есть целиком серой.
  }, [geo, cov, uid, showList, benchmark]);

  return (
    // Высота НЕ фиксируется: виджет в колонке растягивается, а карта раньше держала
    // жёсткие 210–330 px и занимала половину контейнера, оставляя пустоту снизу.
    // Теперь заполняет всё доступное место, с нижней границей на узких экранах.
    <div className="vacmap" ref={boxRef}
      style={{ height: expanded ? "min(70vh, 720px)" : "100%",
               minHeight: expanded ? undefined : 210, flex: expanded ? undefined : "1 1 auto" }}>
      <style>{CSS}</style>
      <div className="mapbox">
        <svg ref={svgRef} viewBox={`0 0 ${VW} ${VH}`} preserveAspectRatio="xMidYMid meet"
          role="img" aria-label={`Карта: ${geo?.district || "район"}, охват вакцинацией по амбулаториям`} />
        <div className="tip" ref={tipRef} hidden />
        {err && (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center",
            justifyContent: "center", color: "#f0908c", fontSize: 12, textAlign: "center", padding: 12 }}>
            Карта не загрузилась: {err}
          </div>
        )}
      </div>
      {showList && <div className="listbox" ref={listRef} />}
    </div>
  );
}
